import { mat3, vec3 } from "gl-matrix"
import { LookTable } from "@/core/types"
import { createLookTable } from "@/core/dcp/look-table"

interface DCPJson {
	CalibrationIlluminant1?: number
	CalibrationIlluminant2?: number
	ColorMatrix1?: mat3
	ColorMatrix2?: mat3
	ForwardMatrix1?: mat3
	ForwardMatrix2?: mat3
	ProfileHueSatMapDims?: vec3
	ProfileLookTableDims?: vec3
	ProfileHueSatMap1?: number[]
	ProfileHueSatMap2?: number[]
	ProfileLookTable?: number[]
}

export interface DCPProfile {
	calibrationTemperature1?: number // in Kelvin
	calibrationTemperature2?: number // in Kelvin
	colorMatrix1?: mat3
	colorMatrix2?: mat3
	forwardMatrix1?: mat3
	forwardMatrix2?: mat3
	// TODO: add reduction and calibration matrices
	hueSatMap1?: LookTable
	hueSatMap2?: LookTable
	lookTable?: LookTable
}

export function parseJsonDCP(dcpJson: DCPJson): DCPProfile {
	const dcp: DCPProfile = {
		calibrationTemperature1: dcpJson.CalibrationIlluminant1,
		calibrationTemperature2: dcpJson.CalibrationIlluminant2,
	}

	if (dcpJson.ColorMatrix1 && dcpJson.ColorMatrix2) {
		dcp.colorMatrix1 = dcpJson.ColorMatrix1
		dcp.colorMatrix2 = dcpJson.ColorMatrix2
	}

	if (dcpJson.ForwardMatrix1 && dcpJson.ForwardMatrix2) {
		dcp.forwardMatrix1 = dcpJson.ForwardMatrix1
		dcp.forwardMatrix2 = dcpJson.ForwardMatrix2
	}

	if (dcpJson.ProfileHueSatMapDims) {
		if (dcpJson.ProfileHueSatMap1) {
			dcp.hueSatMap1 = createLookTable(
				dcpJson.ProfileHueSatMapDims,
				dcpJson.ProfileHueSatMap1,
			)
		}

		if (dcpJson.ProfileHueSatMap2) {
			dcp.hueSatMap2 = createLookTable(
				dcpJson.ProfileHueSatMapDims,
				dcpJson.ProfileHueSatMap2,
			)
		}
	}

	if (dcpJson.ProfileLookTableDims && dcpJson.ProfileLookTable) {
		dcp.lookTable = createLookTable(dcpJson.ProfileLookTableDims, dcpJson.ProfileLookTable)
	}

	return dcp
}

export function interpolateHueSatMap(dcp: DCPProfile, temperature: number): LookTable | undefined {
	const map1 = dcp.hueSatMap1
	const map2 = dcp.hueSatMap2
	const temp1 = dcp.calibrationTemperature1
	const temp2 = dcp.calibrationTemperature2

	if (map1 && !map2) {
		// There is only one table. No interpolation needed
		return map1
	}

	if (!temp1 || !temp2) {
		// No calibration temperatures to use for interpolation
		return map1
	}

	if (!map1 || !map2) {
		return
	}

	if (
		map1.hueDivisions != map2.hueDivisions ||
		map1.satDivisions != map2.satDivisions ||
		map1.valDivisions != map2.valDivisions
	) {
		throw new Error("hueSatMap1 and hueSatMap2 have different sizes")
	}

	let w1: number

	if (temperature <= temp1) {
		w1 = 1
	} else if (temperature >= temp2) {
		w1 = 0
	} else {
		const invT = 1 / temperature
		w1 = (invT - 1.0 / temp2) / (1.0 / temp1 - 1.0 / temp2)
	}

	const w2 = 1 - w1

	const data = new Float32Array(map1.data.length)

	for (let i = 0; i < data.length; i++) {
		data[i] = <number>map1.data[i] * w1 + <number>map2.data[i] * w2
	}

	return {
		...map1,
		data,
	}
}
